/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useEffect } from "react";

import {
  RightSidebar,
  RightSidebarHeader,
  RightSidebarSaveFooter,
  RightSidebarContent
} from "../RightSidebar";

import { ProductSidebarInfo, ProductSidebarImages } from ".";
import {
  ProductSidebarVariants,
  useProductVariants
} from "./ProductSidebarVariants";

import {
  useChangeProduct,
  useGetProductAndCategories,
  useGetAccountFee
} from "./ProductSidebarHooks";

import useProductInfo from "./ProductSidebarInfoHooks";
import useProductImages from "./ProductSidebarImagesHooks";
import LoadingProductSidebar from "./LoadingProductSidebar";
import { Alert, Callout, Intent } from "@blueprintjs/core";
import { Icon } from "../../atoms/Icon";
import styles from "./ProductSidebar.module.scss";

function ProductSidebar({
  productId,
  searchText,
  onDismiss,
  sidebarIsOpen = true
}) {
  const [error, setError] = useState(null);
  const {
    product,
    categories,
    loadingProductOrCategories
  } = useGetProductAndCategories(productId);

  const productSidebarInfoProps = useProductInfo(product);
  const { acceptsDonations, price, hasVariants } = productSidebarInfoProps;

  useEffect(() => {
    if (sidebarIsOpen) {
      productSidebarInfoProps.resetToProduct(product);
    }
  }, [sidebarIsOpen]);

  const {
    fixedFee,
    variableFee,
    ziplineFee,
    isPriceValid,
    minimumPrice
  } = useGetAccountFee(price, acceptsDonations);

  const productSidebarVariantsProps = useProductVariants(product, sidebarIsOpen, minimumPrice);
  const productSidebarImagesProps = useProductImages(product);

  const {
    tabTitles,
    activeTab,
    setActiveTab,
    onSave,
    onDismissSidebar,
    isValidatingForms,
    pageIsLoading,
    disableSave,
    alertConfirmButtonText,
    isAlertOpen,
    onAlertCancel,
    onAlertConfirm,
    alertMessage,
    deleteVariant,
    deleteVariantOption,
    onRemoveImage
  } = useChangeProduct({
    sidebarIsOpen,
    product,
    productId,
    searchText,
    onDismiss,
    setError,
    productSidebarInfoProps,
    productSidebarVariantsProps,
    productSidebarImagesProps
  });

  if (loadingProductOrCategories)
    return (
      <LoadingProductSidebar onDismiss={onDismiss} tabTitles={tabTitles} />
    );

  return (
    <>
      <Alert
        className={styles.confirmAlert}
        cancelButtonText="Cancel"
        confirmButtonText={alertConfirmButtonText}
        icon={
          <Icon
            name="warning-sign"
            color="#7a869b"
            size={32}
            className={styles.confirmAlertIcon}
          />
        }
        intent="danger"
        isOpen={isAlertOpen}
        onCancel={onAlertCancel}
        onConfirm={onAlertConfirm}
        canEscapeKeyCancel
        canOutsideClickCancel
      >
        <p className={styles.confirmAlertMessage}>{alertMessage}</p>
      </Alert>

      <RightSidebar sidebarIsOpen={sidebarIsOpen}>
        <RightSidebarHeader
          title={productId ? "Edit Product" : "Add Product"}
          onDismiss={onDismissSidebar}
          isDismissDisabled={pageIsLoading}
          activeTab={activeTab}
          onTabSelect={setActiveTab}
          tabTitles={tabTitles}
        />

        <RightSidebarContent>
          {error && (
            <Callout title={error} intent={Intent.DANGER} icon="error" />
          )}
          {activeTab === "Information" && (
            <ProductSidebarInfo
              {...productSidebarInfoProps}
              activeOpportunity={product && product.activeOpportunities.length > 0}
              selectableCategories={categories}
              isValidatingForms={isValidatingForms}
              setActiveTab={setActiveTab}
              fixedFee={fixedFee}
              variableFee={variableFee}
              ziplineFee={ziplineFee}
              isPriceValid={isPriceValid}
            />
          )}

          {activeTab === "ProductVariants" && (
            <ProductSidebarVariants
              {...productSidebarVariantsProps}
              deleteVariant={deleteVariant}
              deleteVariantOption={deleteVariantOption}
              isValidatingForms={isValidatingForms}
              minimumPrice={minimumPrice}
            />
          )}

          {activeTab === "Images" && (
            <ProductSidebarImages
              {...productSidebarImagesProps}
              onRemoveImage={onRemoveImage}
            />
          )}
        </RightSidebarContent>

        <RightSidebarSaveFooter
          onCancel={onDismissSidebar}
          onSave={onSave}
          pageIsLoading={pageIsLoading}
          disableSave={disableSave || !(hasVariants || isPriceValid)}
        />
      </RightSidebar>
    </>
  );
}

export default ProductSidebar;
